import { icon, Icon } from "src/controls";
import { Entity, episodeId } from "src/domain/data";
import { StorageProvider } from "src/providers";

export function entities(entries: Entity[], container: HTMLElement, textToHighlight: string) {
	container.innerHTML = '';
	if (entries?.length === 0) {
		container.appendChild(noResults());
		return;
	}
	for (const entry of entries) {
		container.appendChild(entity(entry, textToHighlight));
	}
}

export function entity(entry: Entity, textToHighlight: string): HTMLElement {
	const highlight = (text: string) => highlightText(text, textToHighlight);

	const tagsHtml = [...entry.tags].map(([_, tags]) =>
		tags.map(tag => pill('tag', tag, 'solid')).join('')
	).join('');
	const getIcon = (url: string) => <icon>['fandom', 'miraheze'].find(icon => url.includes(icon)) ?? 'link';
	let linksHtml = entry.links.map(link => Icon.Render(getIcon(link), { link: link }));

	const lastAlias = [...entry.alias].pop()?.[1].slice().pop() ?? '';
	let aliasesHtml = [...entry.alias].map(([_, aliases]) =>
		aliases.map((alias) => `<span class="entry-alias">"${alias}"${alias != lastAlias ? ',' : ''}</span>`).join('')
	).join('');
	if (aliasesHtml.length > 0) {
		aliasesHtml = "" + aliasesHtml + "";
	}

	const depictions = [...entry.depiction].map(([key, value]) => [key, highlight(value)]);
	const latestDepiction = depictions.shift();
	let descriptionHtml = !latestDepiction ? '' :
		`<button class="btn btn-block entry-depiction-latest" data-toggle="collapse" data-target="#entry-depiction-old-${entry.key.replace(' ', '')}" aria-expanded="false" aria-controls="entry-depiction-old-${entry.key.replace(' ', '')}">
            ${latestDepiction?.[1]}
		</button>`
	descriptionHtml += !latestDepiction ? '' : `<div id="entry-depiction-old-${entry.key.replace(' ', '')}" class="collapse entry-depiction-old">${depictions.reverse().map(([episodeKey, depiction]) => `<p>${bullet(episodeId(episodeKey))}<span>${depiction}</span></p>`).join('')}</div>`;

	const connectionsHtml = [...entry.connections].reverse().map(([episodeKey, connections]) =>
		connections.map(connection => `<div class="entry-connection">${bullet(episodeId(episodeKey))}${highlight(connection)}</div>`).join('')
	).join('');

	const entryCard = createDiv('entry-card hover-container', {});
	const entryRow = createDiv('entry-row', { appendTo: entryCard });
	const entryHeader = createDiv('entry-header', { appendTo: entryRow });
	const entryTitle = createDiv('entry-title', { appendTo: entryHeader });
	const entryShare = createDiv('entry-share before', { content: shareButton(entry.key) });
	createDiv('entry-name', { content: [entryShare, `<h2>${entry.key}</h2>`], appendTo: entryTitle });
	createDiv('entry-aliases', { content: aliasesHtml, appendTo: entryTitle });
	createDiv('entry-links', { content: linksHtml, appendTo: entryHeader });
	createDiv('entry-depictions', { content: descriptionHtml, appendTo: entryRow });
	createDiv('entry-connections', { content: connectionsHtml, appendTo: entryCard });
	createDiv('entry-tags', { content: tagsHtml, appendTo: entryCard });

	return entryCard;
}

export function pill(type: 'series' | 'episode' | 'tag', value: string, ...classes: ('mini' | 'solid' | 'light' | '')[]): string {
	if (!value) return '';
	return `<span class="filter-pill ${type} ${classes?.join(' ')}">${value}</span>`;
}

export function noResults(): HTMLElement {
	return createDiv('no-results', { content: 'No entries found. Try adjusting your search or filters.' });
}

export function shareButton(entryKey?: string): HTMLElement {
	return Icon.Render('link', {
		ariaLabel: `Permalink to ${entryKey}`,
		className: 'on-hover',
		target: '_self',
		link: (_ => {
			const newUrlQueryParams = StorageProvider.QuerySearchParams();
			if (entryKey) {
				newUrlQueryParams.set('q', entryKey);
			}
			const url = new URL(window.location.href);
			const newUrl = url.origin + url.pathname + '?' + newUrlQueryParams.toString() + url.hash;
			return newUrl;
		})()
	})
}

function bullet(text: string, title = 'Episode ') {
	return `<i class="bullet" title="${title + text}">${text}</i>`;
}

function highlightText(text: string, term: string): string {
	if (term) {
		const re = new RegExp(term, "gi"); // search for all instances
		const newText = text.replace(re, (match) => `<mark class="p-0">${match}</mark>`);
		return newText;
	}
	return text;
}

function createDiv(className: string, opts?: { content?: string | HTMLElement | (string | HTMLElement)[], appendTo?: HTMLElement }): HTMLElement {
	const result = document.createElement('div');
	result.className = className;
	if (opts?.content) {
		if (!Array.isArray(opts.content)) {
			opts.content = [opts.content];
		}
		for (const item of opts.content) {
			if (typeof item == "string") {
				result.innerHTML += item;
			} else {
				result.appendChild(item);
			}
		}
	}
	if (opts?.appendTo) {
		opts.appendTo.appendChild(result);
	}
	return result;
}